import {
	BASE_PRECISION_EXP,
	BigNum,
	BN,
	PositionDirection,
	PublicKey,
	QUOTE_PRECISION_EXP,
	MarketTypeStr,
} from '@velocity-exchange/sdk';
import {
	UISerializableOrderActionRecord,
	ENUM_UTILS,
} from '@velocity-exchange/common';

export type TradeRecord = Pick<
	UISerializableOrderActionRecord,
	| 'taker'
	| 'maker'
	| 'takerOrderBaseAssetAmount'
	| 'makerOrderBaseAssetAmount'
	| 'baseAssetAmountFilled'
	| 'quoteAssetAmountFilled'
	| 'marketIndex'
	| 'marketType'
	| 'takerOrderDirection'
	| 'makerOrderDirection'
	| 'takerOrderCumulativeBaseAssetAmountFilled'
	| 'makerOrderCumulativeBaseAssetAmountFilled'
	| 'takerOrderCumulativeQuoteAssetAmountFilled'
	| 'makerOrderCumulativeQuoteAssetAmountFilled'
	| 'takerExistingQuoteEntryAmount'
	| 'makerExistingQuoteEntryAmount'
	| 'takerExistingBaseAssetAmount'
	| 'makerExistingBaseAssetAmount'
	| 'ts'
	| 'slot'
	| 'takerFee'
	| 'makerFee'
	| 'txSig'
>;

export type TradeInfo = {
	totalBaseAmountFilled: BigNum;
	totalQuoteAmountFilled: BigNum;
	baseAssetAmount: BigNum;
	baseAssetAmountFilled: BigNum;
	quoteAssetAmountFilled: BigNum;
	existingQuoteEntryAmount: BigNum;
	existingBaseAssetAmount: BigNum;
	direction: PositionDirection | null;
	marketType: MarketTypeStr;
	marketIndex: number;
	ts: BN;
	slot: number;
	fee: BigNum | null;
	counterparty: PublicKey | null;
	isTaker: boolean;
	txSig: string | null;
};

export const getTradeInfoFromOrderActionRecord = (
	user: PublicKey,
	tradeRecord: TradeRecord
): TradeInfo => {
	const isTaker = tradeRecord.taker?.equals(user) ?? false;

	const baseAssetAmount = isTaker
		? tradeRecord.takerOrderBaseAssetAmount
		: tradeRecord.makerOrderBaseAssetAmount;
	const baseAssetAmountFilled = tradeRecord.baseAssetAmountFilled;
	const quoteAssetAmountFilled = tradeRecord.quoteAssetAmountFilled;
	const marketIndex = tradeRecord.marketIndex;
	const marketType = ENUM_UTILS.toStr(tradeRecord.marketType);

	const direction = isTaker
		? tradeRecord.takerOrderDirection
		: tradeRecord.makerOrderDirection;

	const fee = isTaker ? tradeRecord.takerFee : tradeRecord.makerFee;

	const ts = tradeRecord.ts;
	const slot = tradeRecord.slot;

	const counterparty = isTaker ? tradeRecord.maker : tradeRecord.taker;
	const existingQuoteEntryAmount = isTaker
		? tradeRecord.takerExistingQuoteEntryAmount
		: tradeRecord.makerExistingQuoteEntryAmount;
	const existingBaseAssetAmount = isTaker
		? tradeRecord.takerExistingBaseAssetAmount
		: tradeRecord.makerExistingBaseAssetAmount;

	return {
		totalBaseAmountFilled:
			(isTaker
				? tradeRecord.takerOrderCumulativeBaseAssetAmountFilled
				: tradeRecord.makerOrderCumulativeBaseAssetAmountFilled) ??
			BigNum.zero(BASE_PRECISION_EXP),
		totalQuoteAmountFilled:
			(isTaker
				? tradeRecord.takerOrderCumulativeQuoteAssetAmountFilled
				: tradeRecord.makerOrderCumulativeQuoteAssetAmountFilled) ??
			BigNum.zero(QUOTE_PRECISION_EXP),
		baseAssetAmount: baseAssetAmount ?? BigNum.zero(BASE_PRECISION_EXP),
		baseAssetAmountFilled:
			baseAssetAmountFilled ?? BigNum.zero(BASE_PRECISION_EXP),
		quoteAssetAmountFilled:
			quoteAssetAmountFilled ?? BigNum.zero(QUOTE_PRECISION_EXP),
		existingQuoteEntryAmount: existingQuoteEntryAmount as BigNum,
		existingBaseAssetAmount: existingBaseAssetAmount as BigNum,
		direction,
		marketType: marketType as MarketTypeStr,
		marketIndex,
		ts,
		slot,
		fee,
		counterparty,
		isTaker,
		txSig: tradeRecord.txSig,
	};
};
